package com.rosario.juridico.domain

import com.rosario.juridico.platform.FirebaseProxy
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock
import kotlinx.serialization.Serializable

/**
 * Modelo de datos estructurado para una oferta enviada por un profesional a una consulta.
 */
@Serializable
data class OfertaDataModel(
    val ofertaId: String = "",
    val consultaId: String = "",
    val abogadoUid: String = "",
    val montoPropuesto: Double = 0.0,
    val mensaje: String = "",
    val matricula: String? = "",
    val telefono: String? = "",
    val estado: String = "PENDIENTE",
    val fechaOfertaIso: String = Clock.System.now().toString(),
    val intencion: String? = null
)

/**
 * Resultado de operaciones sobre Ofertas / Propuestas.
 */
sealed class OfertasResult<out T> {
    data class Success<out T>(val data: T) : OfertasResult<T>()
    data class Error(val message: String, val cause: Throwable? = null) : OfertasResult<Nothing>()
}

/**
 * Repositorio responsable de la gestión de ofertas de profesionales en la subcolección 'consultas/{consultaId}/ofertas'.
 */
class OfertasRepository {

    /**
     * Guarda una propuesta económica en la subcolección 'consultas/{consultaId}/ofertas'.
     */
    suspend fun enviarOferta(
        consultaId: String,
        montoPropuesto: Double,
        mensaje: String,
    ): OfertasResult<String> = withContext(Dispatchers.Default) {
        val abogadoUid = FirebaseProxy.getCurrentUserId()
            ?: return@withContext OfertasResult.Error(
                message = "Sesión no válida. Inicia sesión para enviar una propuesta.",
            )

        if (FirebaseProxy.isAnonymous()) {
            return@withContext OfertasResult.Error(
                message = "Debes contar con una cuenta registrada para enviar ofertas.",
            )
        }

        try {
            val firestore = Firebase.firestore
            val subcollectionRef = firestore.collection("consultas")
                .document(consultaId)
                .collection("ofertas")

            val newDocRef = subcollectionRef.document

            val oferta = OfertaDataModel(
                ofertaId = newDocRef.id,
                consultaId = consultaId,
                abogadoUid = abogadoUid,
                montoPropuesto = montoPropuesto,
                mensaje = mensaje,
                estado = "PENDIENTE",
            )

            newDocRef.set(oferta)

            OfertasResult.Success(newDocRef.id)
        } catch (e: Exception) {
            OfertasResult.Error(
                message = e.message ?: "Error de red al registrar la oferta en Firestore.",
                cause = e,
            )
        }
    }

    /**
     * Guarda un mensaje inicial (Buzón Ciego) en la subcolección 'consultas/{consultaId}/ofertas'.
     */
    suspend fun enviarMensajeOferta(
        consultaId: String,
        mensaje: String,
        matricula: String,
        telefono: String,
        intencion: String? = null
    ): OfertasResult<String> = withContext(Dispatchers.Default) {
        val abogadoUid = FirebaseProxy.getCurrentUserId()
            ?: return@withContext OfertasResult.Error(
                message = "Sesión no válida. Inicia sesión para enviar un mensaje.",
            )

        if (FirebaseProxy.isAnonymous()) {
            return@withContext OfertasResult.Error(
                message = "Debes contar con una cuenta registrada para enviar mensajes.",
            )
        }

        try {
            val firestore = Firebase.firestore
            val subcollectionRef = firestore.collection("consultas")
                .document(consultaId)
                .collection("ofertas")

            val newDocRef = subcollectionRef.document

            val oferta = OfertaDataModel(
                ofertaId = newDocRef.id,
                consultaId = consultaId,
                abogadoUid = abogadoUid,
                montoPropuesto = 0.0,
                mensaje = mensaje,
                matricula = matricula,
                telefono = telefono,
                estado = "enviada",
                intencion = intencion
            )

            newDocRef.set(oferta)

            OfertasResult.Success(newDocRef.id)
        } catch (e: Exception) {
            OfertasResult.Error(
                message = e.message ?: "Error de red al registrar el mensaje en Firestore.",
                cause = e,
            )
        }
    }
}
